import type { HtsReadinessBand } from "./constants";

// The filter contract the admin Results lists (Assessments and HTS Submissions
// tabs) and their API endpoints both speak. Statuses are derived per row from
// status/review state rather than stored columns — see buildAssessmentListWhere
// (assessment.ts) and buildHtsSubmissionListWhere (htsAdminReview.ts) for how
// each one maps onto a query. Flagged/Score are independent, combinable filters
// (see AdminAssessmentFlaggedFilter/AdminAssessmentScoreBand below) rather than
// additional status values, so an admin can e.g. filter Completed + Flagged +
// Strength at once.
export const AdminAssessmentFilterStatus = {
  InProgress: "in_progress",
  Completed: "completed",
} as const;
export type AdminAssessmentFilterStatus =
  (typeof AdminAssessmentFilterStatus)[keyof typeof AdminAssessmentFilterStatus];

export const AdminAssessmentFlaggedFilter = {
  Yes: "yes",
  No: "no",
} as const;
export type AdminAssessmentFlaggedFilter =
  (typeof AdminAssessmentFlaggedFilter)[keyof typeof AdminAssessmentFlaggedFilter];

// Mirrors the bands getScoreLabel (scoring.ts) renders in the Score column,
// as filterable slugs.
export const AdminAssessmentScoreBand = {
  Strength: "strength",
  Developing: "developing",
  NeedsFocus: "needs_focus",
} as const;
export type AdminAssessmentScoreBand =
  (typeof AdminAssessmentScoreBand)[keyof typeof AdminAssessmentScoreBand];

export const AdminHtsSubmissionFilterStatus = {
  NeedsReview: "needs_review",
  Released: "released",
  NoReviewNeeded: "no_review_needed",
} as const;
export type AdminHtsSubmissionFilterStatus =
  (typeof AdminHtsSubmissionFilterStatus)[keyof typeof AdminHtsSubmissionFilterStatus];

// Caps the free-text search so a pasted blob can't turn into an oversized
// ILIKE pattern — far longer than any name, email, tag, or package name.
export const ADMIN_RESULTS_SEARCH_MAX_LENGTH = 100;

/**
 * Query fields both admin Results list endpoints accept, on top of their own
 * tab-specific filters below.
 */
export interface AdminResultsListQueryBase {
  /** Candidate name/email, partial access-code tag, or package name. */
  search?: string;
  from?: string;
  to?: string;
  sortOrder?: "asc" | "desc";
}

/** The query string the admin Assessments list endpoint accepts. */
export interface AdminAssessmentResultsQuery extends AdminResultsListQueryBase {
  status?: AdminAssessmentFilterStatus;
  flagged?: AdminAssessmentFlaggedFilter;
  scoreBand?: AdminAssessmentScoreBand;
}

/** The query string the admin HTS Submissions list endpoint accepts. */
export interface AdminHtsSubmissionResultsQuery extends AdminResultsListQueryBase {
  status?: AdminHtsSubmissionFilterStatus;
  /** HTS readiness band — same value the Score/Readiness columns render. */
  band?: HtsReadinessBand;
}
